import type { ILiveStream } from "../types/models";

type Row = Record<string, any>;

// Sort by `order`, then by the paired [id, order] tuple's id as a tiebreaker:
// the api has no `orderBy` on overlays/shows (findOne), so Postgres heap
// order can shift after an unrelated UPDATE. Without a tiebreaker, two
// same-order rows returned in a different sequence would produce a
// different (spurious) fingerprint.
const byOrderThenId = (a: readonly [unknown, unknown], b: readonly [unknown, unknown]) =>
  Number(a[1]) - Number(b[1]) || String(a[0]).localeCompare(String(b[0]));

// Review fix round 2 (minor): the tiebreaker here is the *referenced media's*
// id (visualMediaId/audioId, from the `id` callback below), not the join
// row's own id — a join row has no stable id of its own to compare across
// requests otherwise. `overlays()` and `shows` below tie-break on the row's
// own id instead, since those rows do carry one.
const ordered = (rows: Row[] | undefined, id: (row: Row) => unknown) =>
  (rows ?? [])
    .map((row) => [id(row), row.order] as const)
    .sort(byOrderThenId);

const overlays = (rows: Row[] | undefined) =>
  (rows ?? [])
    .slice()
    .sort((a, b) => byOrderThenId([a.id, a.order], [b.id, b.order]))
    .map((o) => [
      o.id, o.type, o.order, o.top, o.left, o.width, o.height,
      o.text, o.fontSize, o.fill, o.settings, o.visualMediaId,
      o.bold, o.italic, o.textDecoration, o.textAlign,
    ]);

/** Ids, order, name and overlays of one playlist/video: what populate reads, minus media metadata. */
const content = (c: Row | null | undefined) =>
  c
    ? {
        name: c.name,
        visual: ordered(c.playlistVisualMedias, (r) => r.visualMediaId ?? r.visualMedia?.id),
        audio: ordered(c.audios, (r) => r.audioId ?? r.audio?.id),
        backgrounds: ordered(c.backgrounds, (r) => r.visualMediaId ?? r.visualMedia?.id),
        overlays: overlays(c.overlays),
      }
    : null;

/**
 * A51: the server data the builder draft is built from
 * (populateLiveStreamSettingsFromLiveStream). Probe dimensions, encode status,
 * URLs and thumbnails are left out, so an upload, probe or encode that bumps
 * contentRevision does not change it.
 *
 * R4 (controller ruling): only ENABLED destinations count, because that's what
 * the draft actually reads (`streamingPlatformsIds`). Otherwise adding or
 * deleting a *disabled* destination in the same tab would flag the user's own
 * edited draft as stale.
 */
export function draftSourceFingerprint(stream: ILiveStream): string {
  const s = stream as unknown as Row;
  return JSON.stringify({
    settings: [
      s.type, s.playlistId, s.videoId, s.jinglePlaylistId, s.jingleVideoId,
      s.secondaryVideoId, s.orientation, s.videoWidth, s.videoHeight,
      s.videoLeft, s.videoTop, s.videoBitrate, s.audioBitrate, s.videoFPS,
      s.videoFadeInDuration, s.videoFadeOutDuration, s.audioMixingEnabled,
      s.audioMixingDuration, s.jingleEvery, s.jinglePlaybackOrder,
      s.secondaryVideoVolume, s.playbackOrder, s.startOption, s.endOption,
      s.startDate ?? null, s.endDate ?? null, s.crfMode,
      // Review fix round 1 (Important #2): populate also reads these.
      s.resolution, s.fps, s.isGST,
      s.termsAgreedAt ?? null, s.contentAgreedAt ?? null,
    ],
    playlist: content(s.playlist),
    video: content(s.video),
    jinglePlaylist: content(s.jinglePlaylist),
    jingleVideo: content(s.jingleVideo),
    secondaryVideo: content(s.secondaryVideo),
    shows: (s.liveStreamScheduledShows ?? [])
      .slice()
      .sort((a: Row, b: Row) => String(a.id).localeCompare(String(b.id)))
      .map((show: Row) => [
        show.id, show.name, show.startMinuteOfDay, show.timezone, show.switchMode,
        show.playlistId, show.videoId, content(show.playlist), content(show.video),
      ]),
    destinations: (s.ownedDestinations ?? [])
      .filter((d: Row) => d.enabled)
      .map((d: Row) => d.id)
      .sort(),
  });
}
